import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, CircularProgress, RadioGroup,
  FormControlLabel, Radio, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, Fade, Divider,
  TextField, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BalanceIcon from "@mui/icons-material/Balance";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";          // ← replaces UpdateIcon
import {
  getPortfolioById, getEqualWeights,
  applyEqualWeights, batchUpdateHoldings,
} from "../../../api/portfolio_api";
import { fmtNumber, fmtPercent } from "./portfolioHelpers";
import { getCurrencySymbol } from "../../../utils/currency";


/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const RebalanceDialog = ({
  open,
  onClose,
  portfolios = [],
  onRebalance,
  portfolioId: singlePortfolioId = null,
}) => {
  const isSingleMode = !!singlePortfolioId;

  const [selectedPortfolioId, setSelectedPortfolioId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [applying, setApplying] = useState(false);
  const [saving, setSaving] = useState(false);   // ← replaces `updating`
  const [rebalanceData, setRebalanceData] = useState(null);
  const [error, setError] = useState("");
  const [portfolioDetails, setPortfolioDetails] = useState({});
  const [editedQuantities, setEditedQuantities] = useState({});
  const [editingRows, setEditingRows] = useState({});
  const [quantityErrors, setQuantityErrors] = useState({});

  const hasEdits = Object.keys(editedQuantities).length > 0;
  const hasErrors = Object.values(quantityErrors).some((e) => !!e);

  const sym = getCurrencySymbol();


  /* ── reset + auto-calculate on open ── */
  useEffect(() => {
    if (!open) return;
    setRebalanceData(null);
    setError("");
    setPortfolioDetails({});
    setEditedQuantities({});
    setEditingRows({});
    setQuantityErrors({});
    setApplying(false);
    setSaving(false);

    if (isSingleMode) {
      setSelectedPortfolioId(singlePortfolioId);
      autoCalculate(singlePortfolioId);
    } else {
      setSelectedPortfolioId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ── load holdings for multi-mode list ── */
  useEffect(() => {
    if (isSingleMode || !selectedPortfolioId) return;
    const load = async () => {
      setLoadingHoldings(true);
      try {
        const res = await getPortfolioById(selectedPortfolioId);
        if (res?.success) {
          setPortfolioDetails((p) => ({ ...p, [selectedPortfolioId]: res.data }));
        }
      } catch (e) {
        console.error("Failed to load holdings:", e);
      } finally {
        setLoadingHoldings(false);
      }
    };
    load();
  }, [selectedPortfolioId, isSingleMode]);

  /* ── reset edits when results change ── */
  useEffect(() => {
    setEditedQuantities({});
    setEditingRows({});
    setQuantityErrors({});
  }, [rebalanceData]);

  const autoCalculate = async (id) => {
    setLoading(true);
    setError("");
    try {
      const result = await getEqualWeights(id);
      if (result.authExpired) { setError("Session expired. Please login again."); return; }
      if (!result.success) { throw new Error(result.message || "Failed to calculate rebalance"); }
      setRebalanceData(result.data);
    } catch (e) {
      setError(e.message || "Failed to calculate rebalance");
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!selectedPortfolioId) return;
    await autoCalculate(selectedPortfolioId);
  };

  const handleClose = () => {
    setSelectedPortfolioId("");
    setRebalanceData(null);
    setError("");
    setPortfolioDetails({});
    setEditedQuantities({});
    setEditingRows({});
    setQuantityErrors({});
    setApplying(false);
    setSaving(false);
    setLoading(false);
    setLoadingHoldings(false);
    onClose();
  };

  /* ── Apply (no edits) — uses applyEqualWeights ── */
  const handleApply = async () => {
    if (!rebalanceData || !selectedPortfolioId) return;
    setApplying(true);
    setError("");
    try {
      const result = await applyEqualWeights(selectedPortfolioId);
      if (result.authExpired) { setError("Session expired. Please login again."); setApplying(false); return; }
      if (!result.success) { throw new Error(result.message || "Failed to apply rebalancing"); }
      onRebalance(selectedPortfolioId, result.data);
      handleClose();
    } catch (e) {
      setError(e.message || "Failed to apply rebalancing");
      setApplying(false);
    }
  };

  /* ── Save All Changes (has edits) ──────────────────────────────────────
     Builds a SINGLE batchUpdateHoldings payload that contains:
       • every edited ticker  → uses the custom quantity the user typed
       • every unedited non-HOLD ticker → uses the rebalanced qty from rebalanceData
     No separate applyEqualWeights call is made.
  ──────────────────────────────────────────────────────────────────────── */
  const handleSaveAll = async () => {
    if (!rebalanceData || !selectedPortfolioId || hasErrors) return;
    setSaving(true);
    setError("");
    try {
      const today = new Date().toISOString().split("T")[0];

      const batchPayload = (rebalanceData.trades || [])
        .filter((t) => t.trade_action !== "HOLD")   // HOLDs need no update
        .map((trade) => {
          const existingShares = Number(trade.current_shares || 0);
          const costPerShare = Number(trade.current_price || 0);

          // Custom qty if user edited this row, otherwise rebalanced qty
          const qty = editedQuantities[trade.ticker] !== undefined
            ? Number(editedQuantities[trade.ticker])
            : Math.abs(Number(trade.trade_shares || 0));

          let finalShares, finalCostBasis;
          if (trade.trade_action === "SELL") {
            finalShares = Math.max(existingShares - qty, 0);
            finalCostBasis = finalShares * costPerShare;
          } else {                                   // BUY
            finalShares = existingShares + qty;
            finalCostBasis = finalShares * costPerShare;
          }

          return {
            ticker: trade.ticker,
            shares: finalShares,
            cost_basis: Math.max(finalCostBasis, 0),
            purchase_date: today,
          };
        });

      const batchRes = await batchUpdateHoldings(selectedPortfolioId, batchPayload);
      if (!batchRes?.success) throw new Error("Failed to save changes");

      onRebalance(selectedPortfolioId, batchRes.data);
      handleClose();
    } catch (e) {
      setError(e.message || "Failed to save changes");
      setSaving(false);
    }
  };

  /* ── Quantity helpers ── */
  const handleQuantityChange = (ticker, value, trade) => {
    // Store raw string so intermediate states ("", "0.", "1.5") aren't destroyed
    if (value === "") {
      setEditedQuantities((p) => ({ ...p, [ticker]: "" }));
      setQuantityErrors((p) => ({ ...p, [ticker]: "Enter a valid number" }));
      return;
    }
    const numVal = parseFloat(value);
    if (isNaN(numVal) || numVal < 0) {
      setQuantityErrors((p) => ({ ...p, [ticker]: "Cannot be negative" }));
      setEditedQuantities((p) => ({ ...p, [ticker]: value }));
      return;
    }
    if (trade.trade_action === "SELL") {
      const max = Number(trade.current_shares || 0);
      if (numVal > max) {
        setQuantityErrors((p) => ({ ...p, [ticker]: `Max ${fmtNumber(max, 2)} shares` }));
        setEditedQuantities((p) => ({ ...p, [ticker]: value }));
        return;
      }
    }
    setQuantityErrors((p) => ({ ...p, [ticker]: null }));
    setEditedQuantities((p) => ({ ...p, [ticker]: value }));
  };

  const toggleEditing = (ticker, trade) => {
    if (trade.trade_action === "HOLD") return;
    setEditingRows((prev) => {
      const isEditing = prev[ticker];
      if (isEditing) {
        setEditedQuantities((p) => { const u = { ...p }; delete u[ticker]; return u; });
        setQuantityErrors((p) => { const u = { ...p }; delete u[ticker]; return u; });
      }
      return { ...prev, [ticker]: !isEditing };
    });
  };

  const getPortfolioInfo = (id) => portfolioDetails[id] || portfolios.find((p) => p.id === id);
  const selectedPortfolio = getPortfolioInfo(selectedPortfolioId);
  const hasHoldings = selectedPortfolio?.holdings?.length > 0;

  // Derived counts for footer label
  const editedCount = Object.keys(editedQuantities).length;
  const rebalancedCount = (rebalanceData?.trades || []).filter(
    (t) => t.trade_action !== "HOLD" && editedQuantities[t.ticker] === undefined
  ).length;

  /* ─────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────── */
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "var(--color-surface)",
          backgroundImage: "none",
          borderRadius: 1.5,
          border: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-xl)",
        },
      }}
    >
      {/* ── HEADER ── */}
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1.25,
          pt: 1.75,
          px: 2.5,
          borderBottom: "1px solid var(--color-border-primary)",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.25}>
          <Box
            sx={{
              width: 32, height: 32, borderRadius: 1,
              bgcolor: "var(--color-surface-hover)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <BalanceIcon sx={{ fontSize: 16, color: "var(--color-primary)" }} />
          </Box>
          <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-text-primary)">
            Equal Weight Rebalancing
          </Typography>
          {hasEdits && (
            <Chip
              label={`${editedCount} Modified`}
              size="small"
              sx={{
                bgcolor: "var(--color-warning)",
                color: "#fff",
                fontSize: "var(--font-size-xs)",
                height: 22,
                fontWeight: "var(--font-weight-semibold)",
              }}
            />
          )}
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          disabled={applying || saving}
          sx={{ color: "var(--color-text-secondary)", p: 0.5, "&:hover": { bgcolor: "var(--color-surface-hover)" } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* ── CONTENT ── */}
      <DialogContent sx={{ px: 2.5, py: 2.5 }}>

        {/* ══ SINGLE MODE: spinner while auto-calculating ══ */}
        {isSingleMode && loading && (
          <Box display="flex" alignItems="center" justifyContent="center" py={6} gap={1.5}>
            <CircularProgress size={20} sx={{ color: "var(--color-primary)" }} />
            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">Calculating rebalance…</Typography>
          </Box>
        )}

        {/* ══ SINGLE MODE: top-level error (no data yet) ══ */}
        {isSingleMode && !loading && error && !rebalanceData && (
          <Alert severity="error" sx={{ my: 2, bgcolor: "var(--color-error-light)", color: "var(--color-text-primary)", border: "1px solid var(--color-error)", "& .MuiAlert-icon": { color: "var(--color-error)" } }}>
            {error}
          </Alert>
        )}

        {/* ══ MULTI MODE: portfolio selection ══ */}
        {!isSingleMode && !rebalanceData && (
          <>
            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" my={2} lineHeight={1.6}>
              Select a portfolio to calculate equal weight rebalancing. This will
              show you the trades needed to make every holding have the same dollar value.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, bgcolor: "var(--color-error-light)", color: "var(--color-text-primary)", border: "1px solid var(--color-error)", "& .MuiAlert-icon": { color: "var(--color-error)" } }}>
                {error}
              </Alert>
            )}

            <RadioGroup value={selectedPortfolioId} onChange={(e) => setSelectedPortfolioId(e.target.value)}>
              <Box sx={{ border: "1px solid var(--color-border-primary)", borderRadius: 1, overflow: "hidden", bgcolor: "var(--color-bg-secondary)", mb: 2 }}>
                {portfolios.map((portfolio) => {
                  const info = getPortfolioInfo(portfolio.id);
                  const totalValue = (info?.holdings || []).reduce((s, h) => s + Number(h.current_value || 0), 0);
                  const holdingCount = info?.holdings?.length || 0;
                  const isSelected = selectedPortfolioId === portfolio.id;
                  const isLoadingThis = isSelected && loadingHoldings;

                  return (
                    <FormControlLabel
                      key={portfolio.id}
                      value={portfolio.id}
                      control={<Radio sx={{ color: "var(--color-text-muted)", "&.Mui-checked": { color: "var(--color-primary)" } }} />}
                      label={
                        <Box py={0.5} display="flex" alignItems="center" gap={1}>
                          <Box flex={1}>
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">{portfolio.name}</Typography>
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">
                              {isLoadingThis ? (
                                <Box component="span" display="flex" alignItems="center" gap={0.5}>
                                  <CircularProgress size={10} sx={{ color: "var(--color-primary)" }} />
                                  <span>Loading holdings…</span>
                                </Box>
                              ) : (
                                <>
                                  {holdingCount} holdings
                                  {holdingCount > 0 && <> • {sym}{fmtNumber(totalValue, 0)} total value</>}
                                </>
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{ width: "100%", m: 0, px: 1.5, py: 1, borderBottom: "1px solid var(--color-border-primary)", "&:last-child": { borderBottom: "none" }, "&:hover": { bgcolor: "var(--color-surface)" } }}
                    />
                  );
                })}
              </Box>
            </RadioGroup>

            {selectedPortfolioId && !loadingHoldings && selectedPortfolio?.holdings?.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2, bgcolor: "var(--color-warning-light)", color: "var(--color-text-primary)", border: "1px solid var(--color-warning)", "& .MuiAlert-icon": { color: "var(--color-warning)" } }}>
                This portfolio has no holdings. Add some holdings before rebalancing.
              </Alert>
            )}
          </>
        )}

        {/* ══ TRADES VIEW ══ */}
        {rebalanceData && !loading && (
          <Fade in timeout={300}>
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2, bgcolor: "var(--color-error-light)", color: "var(--color-text-primary)", border: "1px solid var(--color-error)", "& .MuiAlert-icon": { color: "var(--color-error)" } }}>
                  {error}
                </Alert>
              )}

              {/* Summary Cards */}
              <Box sx={{ bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 2, border: "1px solid var(--color-border-primary)", my: 2 }}>
                <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" lineHeight={1.6}>
                  {rebalanceData.portfolio_name}
                </Typography>

                <Box display="flex" alignItems="stretch" gap={3} flexWrap="wrap">
                  {[
                    { label: "Current Total Value", value: `${sym}${fmtNumber(rebalanceData.total_portfolio_value || 0, 2)}`, color: "var(--color-success)" },
                    { label: "Target Per Holding", value: `${sym}${fmtNumber(rebalanceData.target_value_per_holding || 0, 2)}`, color: "var(--color-primary)" },
                    { label: "Holdings", value: rebalanceData.num_holdings || 0, color: "var(--color-text-primary)" },
                    { label: "Turnover", value: `${fmtNumber(rebalanceData.turnover_pct || 0, 1)}%`, color: "var(--color-warning)" },
                  ].map(({ label, value, color }, i) => (
                    <React.Fragment key={label}>
                      {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: "var(--color-border-primary)", opacity: 0.5 }} />}
                      <Box>
                        <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" my={1} lineHeight={1.6}>{label}</Typography>
                        <Typography fontSize="var(--font-size-sm)" fontWeight={600} color={color}>{value}</Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>

              {/* Required Trades header */}
              <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" mt={2} lineHeight={1.6}>
                Required Trades ({rebalanceData.num_buys || 0} buys, {rebalanceData.num_sells || 0} sells)
              </Typography>

              {/* Trades Table */}
              <Box sx={{ border: "1px solid var(--color-border-primary)", borderRadius: 1, bgcolor: "var(--color-bg-secondary)", maxHeight: 400, overflowY: "auto", overflowX: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {["Ticker", "Action", "Quantity", "Amount", "Current", "Target", "Weight Δ"].map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontSize: "var(--font-size-xs)",
                            fontWeight: "var(--font-weight-semibold)",
                            color: "var(--color-text-secondary)",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid var(--color-border-primary)",
                            bgcolor: "var(--color-surface-hover)",
                            py: 1,
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(rebalanceData.trades || []).map((trade, idx) => {
                      const isBuy = trade.trade_action === "BUY";
                      const isSell = trade.trade_action === "SELL";
                      const isHold = trade.trade_action === "HOLD";
                      const actionColor = isBuy
                        ? "var(--color-success)"
                        : isSell
                          ? "var(--color-error)"
                          : "var(--color-text-secondary)";

                      const isEditing = editingRows[trade.ticker];
                      const editedQty = editedQuantities[trade.ticker];
                      const displayQty = editedQty !== undefined ? editedQty : Math.abs(trade.trade_shares || 0);
                      const qtyError = quantityErrors[trade.ticker];
                      const currentPrice = Number(trade.current_price || 0);
                      const displayAmount = editedQty !== undefined
                        ? editedQty * currentPrice
                        : Math.abs(trade.trade_value || 0);

                      // Highlight row if qty was manually edited
                      const rowIsEdited = editedQuantities[trade.ticker] !== undefined;

                      return (
                        <TableRow
                          key={idx}
                          sx={{
                            "&:hover": { bgcolor: "var(--color-primary-lighter)" },
                            borderBottom: "1px solid var(--color-border-primary)",
                            opacity: isHold ? 0.5 : 1,
                            // subtle left border to indicate edited rows
                            ...(rowIsEdited && {
                              borderLeft: "3px solid var(--color-warning)",
                            }),
                          }}
                        >
                          {/* Ticker */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Typography fontSize="var(--font-size-base)" fontWeight={600} color="var(--color-primary)">
                              {trade.ticker}
                            </Typography>
                            <Typography fontSize="var(--font-size-xs)" color="var(--color-text-secondary)" noWrap>
                              {trade.company_name}
                            </Typography>
                          </TableCell>

                          {/* Action */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              {isBuy ? <TrendingUpIcon sx={{ fontSize: 15, color: actionColor }} /> :
                                isSell ? <TrendingDownIcon sx={{ fontSize: 15, color: actionColor }} /> :
                                  <TrendingFlatIcon sx={{ fontSize: 15, color: actionColor }} />}
                              <Typography fontSize="var(--font-size-base)" fontWeight={600} color={actionColor}>
                                {trade.trade_action}
                              </Typography>
                            </Box>
                          </TableCell>

                          {/* Quantity — editable for BUY/SELL */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Box display="flex" alignItems="center" gap={0.75}>
                              {isEditing && !isHold ? (
                                <Box>
                                  <TextField
                                    size="small"
                                    type="text"
                                    inputMode="decimal"
                                    value={editedQty !== undefined ? editedQty : Math.abs(trade.trade_shares || 0)}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      if (v === "" || /^\d*\.?\d{0,4}$/.test(v)) {
                                        handleQuantityChange(trade.ticker, v, trade);
                                      }
                                    }}
                                    error={!!qtyError}
                                    inputProps={{
                                      style: {
                                        fontSize: "var(--font-size-sm)",
                                        padding: "4px 8px",
                                        width: 80,
                                        color: "var(--color-text-primary)",
                                      },
                                    }}
                                    sx={{
                                      "& .MuiOutlinedInput-root": {
                                        bgcolor: "var(--color-bg-secondary)",
                                        "& fieldset": { borderColor: qtyError ? "var(--color-error)" : "var(--color-border-primary)" },
                                        "&:hover fieldset": { borderColor: qtyError ? "var(--color-error)" : "var(--color-primary)" },
                                        "&.Mui-focused fieldset": { borderColor: qtyError ? "var(--color-error)" : "var(--color-primary)" },
                                      },
                                    }}
                                  />
                                  {qtyError && (
                                    <Typography fontSize="var(--font-size-xs)" color="var(--color-error)" mt={0.25} maxWidth={150}>
                                      {qtyError}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography fontSize="var(--font-size-base)" color={rowIsEdited ? "var(--color-warning)" : "var(--color-text-secondary)"} fontWeight={rowIsEdited ? 600 : 400} noWrap>
                                  {fmtNumber(displayQty, 2)}
                                </Typography>
                              )}

                              {!isHold && (
                                <IconButton
                                  size="small"
                                  onClick={() => toggleEditing(trade.ticker, trade)}
                                  disabled={applying || saving}
                                  sx={{
                                    color: isEditing ? "var(--color-primary)" : "var(--color-text-secondary)",
                                    p: 0.25,
                                    "&:hover": { bgcolor: "var(--color-surface-hover)", color: "var(--color-primary)" },
                                  }}
                                >
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}
                            </Box>
                          </TableCell>

                          {/* Amount */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" noWrap>
                              {sym}{fmtNumber(displayAmount, 2)}
                            </Typography>
                          </TableCell>

                          {/* Current */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" noWrap>
                              {sym}{fmtNumber(trade.current_value || 0, 2)}
                            </Typography>
                            <Typography fontSize="var(--font-size-xs)" color="var(--color-text-secondary)" noWrap>
                              {fmtNumber(trade.current_weight_pct || 0, 1)}%
                            </Typography>
                          </TableCell>

                          {/* Target */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)" noWrap>
                              {sym}{fmtNumber(trade.target_value || 0, 2)}
                            </Typography>
                            <Typography fontSize="var(--font-size-xs)" color="var(--color-text-secondary)" noWrap>
                              {fmtNumber(trade.target_weight_pct || 0, 1)}%
                            </Typography>
                          </TableCell>

                          {/* Weight Δ */}
                          <TableCell sx={{ py: 1, borderBottom: "none" }}>
                            <Typography
                              fontSize="var(--font-size-base)"
                              fontWeight={600}
                              color={trade.weight_change_pct >= 0 ? "var(--color-success)" : "var(--color-error)"}
                            >
                              {trade.weight_change_pct >= 0 ? "+" : ""}{fmtNumber(trade.weight_change_pct || 0, 1)}%
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>

              {/* Info Banner */}
              <Alert
                severity="info"
                sx={{
                  mt: 2,
                  bgcolor: "var(--color-info-light)",
                  color: "var(--color-text-primary)",
                  border: "1px solid var(--color-info)",
                  "& .MuiAlert-icon": { color: "var(--color-info)" },
                }}
              >
                <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">
                  {hasEdits
                    ? `${editedCount} row${editedCount !== 1 ? "s" : ""} manually adjusted · ${rebalancedCount} row${rebalancedCount !== 1 ? "s" : ""} use rebalanced quantity`
                    : "Review before applying"}
                </Typography>
                <Typography fontSize="var(--font-size-base)" color="var(--color-text-secondary)">
                  Total BUY: {sym}{fmtNumber(rebalanceData.total_buy_value || 0, 2)} •&nbsp;
                  Total SELL: {sym}{fmtNumber(rebalanceData.total_sell_value || 0, 2)} •&nbsp;
                  Turnover: {fmtNumber(rebalanceData.turnover_pct || 0, 1)}%
                </Typography>
              </Alert>
            </Box>
          </Fade>
        )}
      </DialogContent>

      {/* ── FOOTER ── */}
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1, borderTop: "1px solid var(--color-border-primary)" }}>

        {/* MULTI MODE — selection step */}
        {!isSingleMode && !rebalanceData && (
          <>
            <Button
              onClick={handleClose}
              disabled={loading || loadingHoldings}
              fullWidth
              sx={{
                textTransform: "none",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-medium)",
                color: "var(--color-text-secondary)",
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCalculate}
              disabled={!selectedPortfolioId || !hasHoldings || loading || loadingHoldings}
              fullWidth
              sx={{
                textTransform: "none",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-medium)",
                bgcolor: "var(--color-primary)",
                boxShadow: "none",
                "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
              }}
            >
              {loading || loadingHoldings
                ? <CircularProgress size={14} color="inherit" />
                : "Calculate Rebalancing"}
            </Button>
          </>
        )}

        {/* SINGLE MODE — cancel always visible */}
        {isSingleMode && (
          <Button
            onClick={handleClose}
            disabled={applying || saving}
            fullWidth
            sx={{
              textTransform: "none",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-medium)",
              color: "var(--color-text-secondary)",
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            }}
          >
            Cancel
          </Button>
        )}

        {/* SHARED trades footer */}
        {rebalanceData && (
          <>
            {/* Back — multi mode only */}
            {!isSingleMode && (
              <Button
                onClick={() => { setRebalanceData(null); setError(""); }}
                disabled={applying || saving}
                fullWidth
                sx={{
                  textTransform: "none",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-medium)",
                  color: "var(--color-text-secondary)",
                  "&:hover": { bgcolor: "var(--color-surface-hover)" },
                }}
              >
                Back
              </Button>
            )}

            {/* ─── KEY CHANGE: two distinct buttons based on whether user has edits ─── */}
            {hasEdits ? (
              /* User edited ≥1 qty → Save All Changes via batchUpdateHoldings */
              <Button
                variant="contained"
                onClick={handleSaveAll}
                disabled={saving || hasErrors}
                fullWidth
                startIcon={
                  saving
                    ? <CircularProgress size={14} color="inherit" />
                    : <SaveIcon sx={{ fontSize: 18 }} />
                }
                sx={{
                  textTransform: "none",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  bgcolor: "var(--color-warning)",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#d97706", boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
                }}
              >
                {saving
                  ? "Saving…"
                  : `Save All Changes (${editedCount} edited · ${rebalancedCount} rebalanced)`}
              </Button>
            ) : (
              /* No edits → standard apply via applyEqualWeights */
              <Button
                variant="contained"
                onClick={handleApply}
                disabled={applying}
                fullWidth
                sx={{
                  textTransform: "none",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  bgcolor: "var(--color-primary)",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
                  "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-secondary)" },
                }}
              >
                {applying
                  ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Applying…</>
                  : "Apply Rebalancing"}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RebalanceDialog;